<template>
  <div class="min-h-screen w-full min-w-0 bg-[#f8f9fa] dark:bg-gray-950">
    <GovUtilityBar fixed @utility-action="goToLogin" />

    <div class="w-full min-w-0 max-w-full flex justify-center px-3 sm:px-4 pt-20 sm:pt-24 pb-10">
      <div class="w-full min-w-0 max-w-md">
        <div class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900 sm:p-8">
          <h1 class="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">Reset Password</h1>
          <p class="mt-1 sm:mt-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300">
            <span v-if="tokenFromUrl">Reset link detected. Enter your new password below.</span>
            <span v-else>Paste the reset token from your email and choose a new password.</span>
          </p>

          <form class="mt-6 space-y-4" @submit.prevent="submit">
            <div v-if="!tokenFromUrl">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" for="token">Reset Token</label>
              <textarea
                id="token"
                name="token"
                v-model="token"
                required
                class="form-input w-full text-base sm:text-sm"
                placeholder="Paste the reset token here"
                rows="3"
              />
            </div>
            <div v-else class="text-xs sm:text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 p-3 rounded-md">
              Reset token detected from email link
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" for="password">New Password</label>
              <input
                id="password"
                name="password"
                v-model="password"
                type="password"
                required
                autocomplete="new-password"
                class="form-input w-full min-h-[44px] text-base sm:text-sm"
                placeholder="Enter your new password"
              />
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                At least 8 characters with uppercase, lowercase, a number, and a special character.
              </p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" for="confirmPassword">Confirm Password</label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                v-model="confirmPassword"
                type="password"
                required
                autocomplete="new-password"
                class="form-input w-full min-h-[44px] text-base sm:text-sm"
                placeholder="Re-enter your new password"
              />
            </div>

            <div
              v-if="message"
              class="text-sm p-3 rounded-md"
              :class="success ? 'text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20' : 'text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20'"
            >
              {{ message }}
              <div v-if="!success && message.toLowerCase().includes('expired')" class="mt-2">
                <router-link to="/forgot-password" class="text-primary-600 dark:text-primary-400 hover:underline font-medium">
                  Request a new password reset link →
                </router-link>
              </div>
            </div>

            <button
              type="submit"
              :disabled="loading"
              class="btn-primary w-full min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed py-3 touch-manipulation"
            >
              <span v-if="loading" class="inline-flex items-center justify-center">
                <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Resetting...
              </span>
              <span v-else>Reset Password</span>
            </button>

            <router-link to="/login" class="block text-center text-xs sm:text-sm text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300 transition-colors">
              Back to login
            </router-link>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import GovUtilityBar from '../components/GovUtilityBar.vue'
import { authService } from '../services/auth'
import { readResetTokenFromLocation } from '../utils/passwordResetUrl'

const router = useRouter()
const route = useRoute()
const token = ref('')
const password = ref('')
const confirmPassword = ref('')
const loading = ref(false)
const message = ref('')
const success = ref(false)

const tokenFromUrl = computed(() => Boolean(route.query.token))

function goToLogin() {
  router.push('/login')
}

onMounted(() => {
  const tokenParam = route.query.token ?? readResetTokenFromLocation()
  if (typeof tokenParam === 'string' && tokenParam.trim()) {
    token.value = tokenParam.trim()
  }
})

const submit = async () => {
  if (password.value !== confirmPassword.value) {
    message.value = 'Passwords do not match.'
    success.value = false
    return
  }

  loading.value = true
  message.value = ''
  success.value = false
  try {
    const ok = await authService.resetPassword(token.value.trim(), password.value)
    success.value = ok
    message.value = ok ? 'Password updated. Redirecting to login...' : 'Failed to reset password.'
    if (ok) {
      setTimeout(() => router.push('/login'), 1000)
    }
  } catch (error) {
    message.value = error instanceof Error ? error.message : 'Failed to reset password.'
    success.value = false
  } finally {
    loading.value = false
  }
}
</script>
